import Button from 'components/base/Button';
import DatePicker from 'components/base/DatePicker';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import { pagoInicial, Pago } from 'data/pagos/pago';
import { TipoDeGasto } from 'data/pagos/tipoDeGasto';
import { useAuth } from 'providers/AuthProvider';
import { Proceso } from 'data/project-management/procesos';

interface UsuarioCaja { id: number; usuario: string; caja: string; }

const CreatePago = () => {
  const navigate = useNavigate();
  const { pagoId } = useParams<{ pagoId: string }>();
  const isEdit = !!pagoId;
  const { user } = useAuth();

  const determineOriginalEstado = (nivel_acceso: string | undefined): string => {
  return nivel_acceso === 'pagos' ? 'Pendiente' : 'Pagado';
  };

  //const [pago, setPago] = useState<Pago>(pagoInicial);
  const [originalEstado, setOriginalEstado] = useState<string>('');
  const [tiposDeGastos, setTiposDeGastos] = useState<TipoDeGasto[]>([]);
  const [procesos, setProcesos] = useState<Proceso[]>([]);
  const [availableCajas, setAvailableCajas] = useState<string[]>([]);
  const [loadingCajas, setLoadingCajas] = useState(true);
  const [loadingGastos, setLoadingGastos] = useState(true);
  const [loadedProcesos, setLoadedProcesos] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pago, setPago] = useState<Pago>(() => {
  const inicial = { ...pagoInicial };
  
  if (!isEdit && (user?.nivel_acceso === 'usuario' || user?.nivel_acceso === 'LA')) {
    inicial.estado = 'Pagado';
  }

  return inicial;
  });

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000';

  // === FETCH CAJAS DISPONIBLES (SIN caja_general) ===
  useEffect(() => {
    if (!user?.iniciales) return;

    const fetchAvailableCajas = async () => {
      setLoadingCajas(true);
      try {
        const response = await axios.get<UsuarioCaja[]>(
          `${apiUrl}/usuario_cajas_m2m/usuario/${user.iniciales}`
        );
        let cajas = response.data.map(item => item.caja);
        cajas = cajas.filter(c => c !== 'caja_general');

        if (cajas.length === 0 && user.nivel_acceso === 'socio') {
          cajas = [user.iniciales];
        }

        setAvailableCajas(cajas);

        if (!isEdit && !pago.caja && cajas.length > 0) {
          const defaultCaja = cajas.includes(user.iniciales) ? user.iniciales : cajas[0];
          setPago(prev => ({ ...prev, caja: defaultCaja }));
        }
      } catch (err) {
        console.error('Error fetching cajas:', err);
        const fallback = user.iniciales;
        setAvailableCajas([fallback]);
        if (!isEdit) setPago(prev => ({ ...prev, caja: fallback }));
      } finally {
        setLoadingCajas(false);
      }
    };

    fetchAvailableCajas();
  }, [user?.iniciales, user?.nivel_acceso, isEdit, apiUrl]);

  // 1. NUEVO useEffect: fuerza la caja para usuarios normales
  useEffect(() => {
    if (user?.nivel_acceso === 'usuario' && user?.iniciales) {
      setPago(prev => ({
        ...prev,
        caja: user.iniciales
      }));
    }
  }, [user?.nivel_acceso, user?.iniciales]);

  // === FETCH TIPOS DE GASTO (SIEMPRE filtrados) ===
  useEffect(() => {
    const fetchGastos = async () => {
      setLoadingGastos(true);
      try {
        const nivel = user?.nivel_acceso || 'usuario';
        const iniciales = user?.iniciales || 'usuario';

        const res = await axios.get<TipoDeGasto[]>(`${apiUrl}/gastos/filtrados`, {
          params: { nivel, iniciales },
          withCredentials: true
        });
        setTiposDeGastos(res.data || []);
      } catch (err) {
  console.error('Error loading gastos:', err);
  const error = err as AxiosError;
  if (error.response?.status === 401) {
    navigate('/login');
  }
  setTiposDeGastos([]);
} finally {
        setLoadingGastos(false);
      }
    };

    if (user?.iniciales) {
      fetchGastos();
    }
  }, [user?.iniciales, user?.nivel_acceso, apiUrl, navigate]);

  // === FETCH PAGO EXISTENTE (EDIT MODE) ===
  useEffect(() => {
    if (!isEdit || !pagoId) return;

    const fetchPago = async () => {
      try {
        const res = await axios.get(`${apiUrl}/pagos/id/${pagoId}`);
        const fetched = res.data;
        console.log('Fetched pago:', fetched);
        setPago({
          ...pagoInicial,
          ...fetched,
          importe: Number(fetched.importe) || 0,
          fechadepago: fetched.fechadepago ? String(fetched.fechadepago) : '',
          factura: fetched.factura || null,
          documento: fetched.documento || null,
          comprobante: fetched.comprobante || null,
        });
        setOriginalEstado(fetched.estado || '');
      } catch (err) {
        setError('No se pudo cargar el pago');
        navigate('/apps/pagos/pagos-generales-list-view');
      }
    };
    fetchPago();
  }, [pagoId, isEdit, navigate, apiUrl]);

  useEffect(() => {
    if (!isEdit && user?.nivel_acceso) {
      const estado = determineOriginalEstado(user.nivel_acceso);
      setOriginalEstado(estado);
    }
  }, [isEdit, user?.nivel_acceso]);


  const canEditEstado = () => {
      if (!user?.nivel_acceso) return false;
      if (['socio', 'pagos'].includes(user.nivel_acceso)) return true;
      if (user.nivel_acceso === 'usuario') {
        return true;
      }
      return false;
  };

  const isFormLocked = isEdit && 
    originalEstado === 'Pagado' && 
    !['socio'].includes(user?.nivel_acceso || '');

  // === FETCH PROCESOS ===
  const fetchProcesos = async () => {
    if (loadedProcesos) return;
    try {
      const res = await axios.get(`${apiUrl}/procesos/procesoscjson`);
      setProcesos(res.data);
      setLoadedProcesos(true);
    } catch (err) {
      console.error('Error fetching procesos:', err);
    }
  };

  const getFormattedDate = () => {
    const date = new Date();
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getFullYear()).slice(-2)}`;
  };

  const handleTGastoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const gastoSeleccionado = e.target.value; // ← valor que viene del <option>
    const tipo = tiposDeGastos.find(t => t.gasto === gastoSeleccionado);
  
  if (!tipo) return;

  setPago(prev => ({
    ...prev,
    gasto: gastoSeleccionado,           // ← NUEVO: guardamos el gasto tal como está en las options
    gastoIdFkEnPagos: tipo.gastoId,
    concepto: tipo.concepto,
    categoria: tipo.concepto,
    pagoLabel: `${tipo.gasto} - ${getFormattedDate()} - usuario: ${user?.iniciales}`
  }));
  };

  const handleImporteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPago(prev => ({ ...prev, importe: Number(e.target.value) || 0 }));
  };

  const handleAclaracionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPago(prev => ({ ...prev, aclaracion: e.target.value }));
  };

  const handleEstadoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nuevoEstado = e.target.value;



    if (isEdit && user?.nivel_acceso === 'usuario' && originalEstado === 'Pagado') {
      alert('No puedes modificar un pago ya marcado como Pagado.');
      return;
    }

    const fechadepago = nuevoEstado === 'Pagado' && !pago.fechadepago
      ? new Date().toISOString().split('T')[0]
      : pago.fechadepago;

    setPago(prev => ({
      ...prev,
      estado: nuevoEstado,
      fechadepago
    }));
  };

  useEffect(() => {
    if (!isEdit && user?.nivel_acceso === 'usuario' && !pago.estado) {
    setPago(prev => ({
      ...prev,
      estado: 'Pagado',
      fechadepago: prev.fechadepago || new Date().toISOString().split('T')[0]
    }));
  }
  }, [isEdit, user?.nivel_acceso]);

  const handlePagaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPago(prev => ({ ...prev, caja: e.target.value }));
  };

  const handleFechadepagoChange = (dates: Date[]) => {
    const date = dates?.[0];
    setPago(prev => ({
      ...prev,
      fechadepago: date ? date.toISOString().split('T')[0] : ''
    }));
  };

  const handleFileChange = (field: 'factura' | 'documento' | 'comprobante') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) setPago(prev => ({ ...prev, [field]: file }));
  };

  const handleProcesoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPago(prev => ({ ...prev, proceso: e.target.value }));
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    const confirm = window.confirm(
      `¿Confirmas ${isEdit ? 'actualizar' : 'crear'} el pago?\n${pago.pagoLabel || ''}\nImporte: $${pago.importe}`
    );
    if (!confirm) return;

    // --- NUEVA LÓGICA DE ESTADO Y CAJA ---
    let finalEstado = pago.estado || 'Pendiente';
    let finalCaja = pago.caja || '';

    // Mantenemos la regla previa para LA/supervisor si existía
    if (user?.nivel_acceso === 'LA' || user?.nivel_acceso === 'supervisor') {
      finalEstado = 'Pagado';
    }

    if (user?.nivel_acceso === 'usuario') {
      // Reglas estrictas para 'usuario'
      if (finalEstado.toLowerCase() === 'pendiente') {
        finalCaja = '';
      } else if (finalEstado.toLowerCase() === 'pagado') {
        finalCaja = user.iniciales || '';
      }
    } 
    
    // ==========================================
    // DEBUG LOGS PARA VERIFICAR LA CAJA Y EL USER
    // ==========================================
    console.log('--- DEBUG SUBMIT ---');
    console.log('1. User Actual:', { iniciales: user?.iniciales, nivel_acceso: user?.nivel_acceso });
    console.log('2. Estado de Pago original:', { estado: pago.estado, caja: pago.caja });
    console.log('3. Variables calculadas:', { finalEstado, finalCaja });
    console.log('4. Valor EXACTO que irá a FormData (Caja):', finalCaja ? finalCaja.toUpperCase() : '');
    console.log('--------------------');
    // ==========================================

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('pagoLabel', pago.pagoLabel || '');
      formData.append('gastoIdFkEnPagos', String(pago.gastoIdFkEnPagos || ''));
      formData.append('concepto', pago.concepto || '');
      formData.append('importe', String(pago.importe));
      formData.append('aclaracion', pago.aclaracion || '');
      
      // Pasamos las variables evaluadas
      formData.append('estado', finalEstado);
      formData.append('caja', finalCaja ? finalCaja.toUpperCase() : '');
      
      formData.append('fechadepago', pago.fechadepago ? String(pago.fechadepago) : '');
      formData.append('usuario', user?.iniciales || 'unknown');
      formData.append('proceso', pago.proceso || '');

      if (pago.factura instanceof File) formData.append('factura', pago.factura);
      if (pago.documento instanceof File) formData.append('documento', pago.documento);
      if (pago.comprobante instanceof File) formData.append('comprobante', pago.comprobante);

      if (isEdit) {
        await axios.put(`${apiUrl}/pagos/update2/${pagoId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${apiUrl}/pagos/store`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      navigate(-1);
    }  catch (err) {
      const error = err as AxiosError;
      setError(`Error al ${isEdit ? 'actualizar' : 'crear'} el pago: ${error.message}`);
      console.error('Error en submit:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="p-4 text-center">
        <h3>Error</h3>
        <p>{error}</p>
        <Button variant="secondary" onClick={() => navigate(-1)}>Volver</Button>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="mb-4">
        {isEdit ? 'Editar' : 'Crear'} Pago: {pago.pagoLabel}
      </h2>

      {isFormLocked && (
        <div className="alert alert-warning">
          <strong>Este pago está marcado como "Pagado"</strong> y no se puede modificar.
        </div>
      )}

      <Row as="form" className="g-3 mb-6">
        <Col sm={6} md={4}>
          <Form.Label>Tipo de Gasto</Form.Label>
          {loadingGastos ? (
            <div className="d-flex align-items-center">
              <Spinner size="sm" className="me-2" />
              <span>Cargando...</span>
            </div>
          ) : (
            <Form.Select
              size="lg"
              onChange={handleTGastoChange}
              value={pago.gasto || ''}
              disabled={isEdit || isFormLocked}
            >
              <option value="">Seleccionar</option>
              {tiposDeGastos.map(t => (
                <option key={t.gastoId} value={t.gasto}>
                  {t.gasto}
                </option>
              ))}
            </Form.Select>
          )}
        </Col>

        <Col sm={6} md={4}>
          <Form.Label>Importe</Form.Label>
          <Form.Control
            size="lg"
            type="number"
            value={pago.importe || ''}
            onChange={handleImporteChange}
            disabled={isFormLocked}
          />
        </Col>

  <Col sm={6} md={4}>
    <Form.Label>Quien Paga</Form.Label>
    {loadingCajas ? (
      <div className="d-flex align-items-center">
        <Spinner size="sm" className="me-2" />
        <span>Cargando cajas...</span>
      </div>
    ) : user?.nivel_acceso === 'usuario' ? (
      <div className="form-control form-control-lg bg-dark text-light fw-bold">
        {user.iniciales.toUpperCase()}
      </div>
    ) : (
      <Form.Select
        size="lg"
        value={pago.caja || ''}
        onChange={handlePagaChange}
        // MODIFICACIÓN: Si es socio, no se deshabilita por isEdit
        disabled={(isEdit && user?.nivel_acceso !== 'socio') || isFormLocked}
      >
        <option value="">Seleccionar caja</option>
        {availableCajas.map(c => (
          <option key={c} value={c}>
            {c.toUpperCase().replace('_', ' ')}
          </option>
        ))}
      </Form.Select>
    )}
  </Col>


<Col sm={6} md={4}>
  <Form.Label>Estado</Form.Label>

  {['socio', 'pagos', 'usuario'].includes(user?.nivel_acceso || '') ? (
    /* Usuarios con control total: socio y pagos */
    <Form.Select
      size="lg"
      value={pago.estado}
      onChange={handleEstadoChange}
      disabled={!canEditEstado() || isFormLocked}
    >
      <option value="Pendiente">Pendiente</option>
      <option value="Pagado">Pagado</option>
      <option value="No Pagamos">No Pagamos</option>
    </Form.Select>
  ) : (
    /* Resto de usuarios: solo ven "Pagado" como etiqueta */
    <div className="form-control form-control-lg bg-dark text-light fw-bold">
      Pagado
    </div>
  )}
</Col>
        <Col sm={6} md={4}>
          <Form.Label>Fecha de Pago</Form.Label>
          <DatePicker
            value={pago.fechadepago ? new Date(pago.fechadepago) : undefined}
            onChange={handleFechadepagoChange}
            render={(_, ref) => (
              <Form.Control
                type="date"
                ref={ref}
                disabled={isEdit || isFormLocked}
              />
            )}
          />
        </Col>

        <Col sm={6} md={4}>
          <Form.Label>Factura</Form.Label>
          {/* Si hay una factura guardada (es un string de la URL), mostramos el botón */}
          {pago.factura && typeof pago.factura === 'string' && (
            <div className="mb-2">
              <a 
                href={pago.factura} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn btn-sm btn-outline-primary"
              >
                📄 Ver Factura Actual
              </a>
            </div>
          )}
          {/* Input para subir o reemplazar (solo visible si no está bloqueado) */}
          {!isFormLocked && (
            <Form.Control 
              type="file" 
              size="lg" 
              onChange={handleFileChange('factura')} 
            />
          )}
        </Col>

        <Col sm={6} md={4}>
          <Form.Label>Comprobante</Form.Label>
          {/* Si hay un comprobante guardado en S3, mostramos el botón para abrirlo */}
          {pago.comprobante && typeof pago.comprobante === 'string' && (
            <div className="mb-2">
              <a 
                href={pago.comprobante} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn btn-sm btn-outline-primary"
              >
                👁️ Ver Comprobante Actual
              </a>
            </div>
          )}
          {/* Input para subir o reemplazar (solo visible si no está bloqueado) */}
          {!isFormLocked && (
            <Form.Control 
              type="file" 
              size="lg" 
              onChange={handleFileChange('comprobante')} 
            />
          )}
        </Col>


        <Col sm={6} md={4}>
          <Form.Label>Proceso</Form.Label>
          <Form.Select
            size="lg"
            onFocus={fetchProcesos}
            onChange={handleProcesoChange}
            value={pago.proceso || ''}
            disabled={isEdit || isFormLocked}
          >
            <option value="">Seleccionar</option>
            {procesos.map(p => (
              <option key={p.PROC} value={p.PROC}>{p.AUX8 || p.AUX7}</option>
            ))}
          </Form.Select>
        </Col>

        <Col sm={6} md={4}>
          <Form.Label>Aclaración</Form.Label>
          <Form.Control
            as="textarea"
            size="lg"
            value={pago.aclaracion || ''}
            onChange={handleAclaracionChange}
            disabled={isFormLocked}
          />
        </Col>

        <Col xs={12} className="d-flex justify-content-end gap-3">
          <Button variant="secondary" onClick={() => navigate(-1)} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || isFormLocked}
          >
            {isSubmitting ? 'Guardando...' : isEdit ? 'Actualizar' : 'Guardar'}
          </Button>
        </Col>
      </Row>
    </div>

    
  
  );
};

export default CreatePago;